import React, { useState, useRef, useEffect } from "react";
import { BsThreeDots } from "react-icons/bs";
import { MdContentCopy } from "react-icons/md";
import { FaHeart, FaThumbsUp, FaLaugh, FaSadTear } from "react-icons/fa";
import { BiTime } from "react-icons/bi";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import AudioMessage from "./AudioMessage";
import MessageReactions from "./MessageReactions";
import PlayEmoteButton from "./PlayEmoteButton";
import { formatTimestamp } from "../utils/chatUtils";
import ReactMarkdown from "react-markdown";

const ChatMessage = ({ 
  message, 
  token, 
  api, 
  onDeleteMessage, 
  index, 
  onPlayEmote, 
  isMobile, 
  onReact, 
  isGrouped = false, 
  showTimestamp = true 
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [showThreeDots, setShowThreeDots] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const hasEmote = message.role === 'assistant' && message.emote_type;
  const isAudioMessage = message.type === 'AUDIO_MESSAGE';
  const isLongMessage = message.content && message.content.length > 1000;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDeleteMessage = async (messageItem) => {
    if (token && typeof onDeleteMessage === 'function') {
      try {
        console.log('message id', messageItem);
        const response = await fetch(`${api}/delete_message`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ 
            message_id: messageItem.id 
          })
        });
        
        if (response.ok) {
          onDeleteMessage(messageItem);
          setIsMenuOpen(false);
        }
      } catch (error) {
        console.error("Error deleting message:", error);
      }
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
        setShowThreeDots(false);
        setShowReactions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Brand-aligned chat bubbles:
  //   • user → solid terra (the brand accent), cream text — feels like
  //     the user's voice radiating in the conversation
  //   • assistant → warm parchment-on-brown (lifted brown surface with
  //     cream text), so AI replies feel like a journal entry, not a
  //     generic chatbot card
  const messageStyle = {
    alignSelf: message.role === "user" ? "flex-end" : "flex-start",
    maxWidth: isMobile ? "85%" : "78%",
    background: message.role === "user"
      ? "linear-gradient(135deg, #3b725f 0%, #4a8a73 100%)"
      : "linear-gradient(180deg, rgba(56, 51, 41, 0.92) 0%, rgba(46, 41, 33, 0.92) 100%)",
    color: message.role === "user" ? "#fefcf6" : "rgba(254, 252, 246, 0.95)",
    padding: isAudioMessage ? "10px 14px" : "12px 16px",
    borderRadius: message.role === "user"
      ? (isGrouped ? "18px 18px 4px 18px" : "18px 18px 4px 18px")
      : (isGrouped ? "18px 18px 18px 4px" : "18px 18px 18px 4px"),
    wordWrap: "break-word",
    boxShadow: message.role === "user"
      ? "0 4px 14px rgba(59, 114, 95, 0.28), 0 0 0 1px rgba(232, 199, 123, 0.08)"
      : "0 3px 12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(254, 252, 246, 0.04)",
    border: "none",
    fontSize: 15,
    lineHeight: 1.5,
    letterSpacing: "0.1px",
    textAlign: "left",
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    transform: 'translateY(0)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)'
  };

  const actionButtonsStyle = {
    position: "absolute", 
    top: "-8px", 
    right: message.role === "user" ? "-8px" : "auto",
    left: message.role === "assistant" ? "-8px" : "auto",
    display: 'flex',
    gap: '4px',
    background: 'rgba(0,0,0,0.8)',
    borderRadius: '12px',
    padding: '4px',
    zIndex: 10
  };

  const reactionsPanelStyle = {
    position: "absolute",
    top: "-45px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(0,0,0,0.9)",
    borderRadius: "20px",
    padding: "8px 12px",
    display: "flex",
    gap: "8px",
    zIndex: 15,
    animation: 'scaleIn 0.2s ease-out'
  };

  const dropdownMenuStyle = {
    position: "absolute",
    top: "100%",
    right: message.role === "user" ? "0" : "auto",
    left: message.role === "assistant" ? "0" : "auto",
    marginTop: "4px",
    background: "rgba(255, 255, 255, 0.95)",
    border: "1px solid rgba(255, 255, 255, 1)",
    borderRadius: "8px",
    zIndex: 100,
    minWidth: "140px",
    color: "black",
    fontSize: 14,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const renderReactionButton = ({ icon: Icon, key, color }) => (
    <button
      key={key}
      onClick={() => {
        onReact && onReact(message.id || `${index}-${message.timestamp}`, key);
        setShowReactions(false);
      }}
      style={{
        background: 'none',
        border: 'none',
        color: color,
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        width: '28px',
        height: '28px'
      }}
      onMouseEnter={(e) => {
        e.target.style.background = `${color}30`;
        e.target.style.transform = 'scale(1.2)';
      }}
      onMouseLeave={(e) => {
        e.target.style.background = 'none';
        e.target.style.transform = 'scale(1)';
      }}
    >
      <Icon size={16} />
    </button>
  );

  const reactionOptions = [
    { icon: FaThumbsUp, key: 'like', color: '#4CAF50' },
    { icon: FaHeart, key: 'love', color: '#E91E63' },
    { icon: FaLaugh, key: 'laugh', color: '#FF9800' },
    { icon: FaSadTear, key: 'sad', color: '#2196F3' }
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: message.role === "user" ? "flex-end" : "flex-start",
        marginBottom: isGrouped ? '2px' : '8px',
        opacity: 0,
        animation: 'slideIn 0.3s ease-out forwards',
        animationDelay: `${index * 0.05}s`
      }}
    >
      {/* Message bubble */}
      <div
        key={index}
        className={`message ${message.role}`}
        style={messageStyle}
        onMouseEnter={() => {
          if (message.id || message.content) {
            setShowThreeDots(true);
          }
        }}
        onMouseLeave={() => {
          if (!isMenuOpen && !showReactions) {
            setShowThreeDots(false);
          }
        }}
      >
        {/* Message content */}
        {isAudioMessage ? (
          <AudioMessage audioUrl={message.audioUrl} index={index} role={message.role} />
        ) : (
          <span style={{ lineHeight: '1.4' }}>
            <ReactMarkdown>
              {isLongMessage && !isExpanded 
                ? message.content.substring(0, 1000) + '...' 
                : message.content}
            </ReactMarkdown>
            
            {isLongMessage && (
              <div 
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '8px',
                  cursor: 'pointer',
                  color: "rgba(254, 252, 246, 0.85)",
                  fontWeight: 500,
                  fontSize: '13px',
                  padding: '5px 10px',
                  borderRadius: '6px',
                  background: message.role === "user"
                    ? "rgba(254, 252, 246, 0.18)"
                    : "rgba(254, 252, 246, 0.08)",
                }}
              >
                {isExpanded ? (
                  <>
                    <IoIosArrowUp style={{ marginRight: '4px' }} />
                    Show Less
                  </>
                ) : (
                  <>
                    <IoIosArrowDown style={{ marginRight: '4px' }} />
                    Read More ({Math.round((message.content.length - 1000) / 1000)}K more characters)
                  </>
                )}
              </div>
            )}
          </span>
        )}
        
        {/* Timestamp */}
        {showTimestamp && message.timestamp && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginTop: hasEmote || !isAudioMessage ? '6px' : '4px',
            fontSize: '11px',
            opacity: 0.65,
            color: message.role === "user" ? "rgba(254, 252, 246, 0.85)" : "rgba(254, 252, 246, 0.65)"
          }}>
            <BiTime size={10} />
            <span>{formatTimestamp(message.timestamp)}</span>
          </div>
        )}
        
        {/* Play emote button */}
        {hasEmote && (
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            marginTop: '6px'
          }}>
            <PlayEmoteButton 
              emoteType={message.emote_type} 
              onClick={() => onPlayEmote(message.emote_type)} 
            />
          </div>
        )}

        {/* Reactions */}
        {message.reactions && Object.keys(message.reactions).length > 0 && (
          <MessageReactions 
            messageId={message.id || `${index}-${message.timestamp}`} 
            reactions={message.reactions} 
            onReact={onReact} 
          />
        )}

        {/* Action buttons */}
        {showThreeDots && (
          <div style={actionButtonsStyle}>
            {/* Copy button */}
            {!isAudioMessage && (
              <button
                onClick={() => copyToClipboard(message.content)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: copied ? '#4CAF50' : 'white',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  transition: 'color 0.2s'
                }}
                title={copied ? "Copied!" : "Copy message"}
              >
                <MdContentCopy size={14} />
              </button>
            )}

            {/* React button */}
            <button
              onClick={() => setShowReactions(!showReactions)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px'
              }}
              title="Add reaction"
            >
              <FaHeart size={12} />
            </button>

            {/* Menu button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center'
              }}
              title="More options"
            >
              <BsThreeDots size={12} />
            </button>
          </div>
        )}

        {/* Reactions panel */}
        {showReactions && (
          <div style={reactionsPanelStyle}>
            {reactionOptions.map(renderReactionButton)}
          </div>
        )}

        {/* Dropdown menu */}
        {isMenuOpen && (
          <div ref={menuRef} style={dropdownMenuStyle}>
            {message.id && (
              <div 
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderRadius: "6px",
                  margin: "4px",
                  transition: 'background 0.2s'
                }}
                onClick={() => handleDeleteMessage(message)}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,0,0,0.1)'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                Delete Message
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage; 